import styled from '@emotion/styled';
import {Container, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {BlockIntro} from 'UI/components/layout/BlockIntro';
import {Card} from 'UI/components/layout/Card';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {H4} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';

import type {CardGridBlockType, ModuleIntro} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * Array of content items containing image data, titles, and optional descriptions for grid display.
     * Each item represents a single grid cell with its associated visual and textual content.
     * The cards array determines the number of grid items and their individual content structure.
     */
    cards: CardGridBlockType['cards'];
    /**
     * Unique identifier for the image grid block used for block intro functionality and component identification.
     * This identifier helps with content management and enables proper block-level configuration within the CMS.
     * The ID is essential for linking the block intro section to the corresponding content block.
     */
    id: string;
    /**
     * Module intro configuration object containing introductory content and settings for the block.
     * This object defines how the block introduction should be displayed above the image grid content.
     * The intro provides contextual information and helps establish the purpose of the card grid section.
     */
    intro: ModuleIntro;
    /**
     * The `testId` property represents a unique identifier for testing purposes to enable reliable component identification during testing.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     * The test identifier helps maintain consistent testing practices across the application components.
     *
     * @default 'CardGridBlock'
     */
    testId?: string;
}

/**
 * The CardGridBlock component renders a grid layout of cards containing images, titles, and optional descriptions with an introductory section.
 * This component is designed to display content in a responsive grid format where each card represents a distinct piece of information or content item.
 * The component utilizes MobX observer pattern for reactive updates and integrates with the CMS block system for content management capabilities.
 *
 * @param props        The component props.
 * @param props.cards  Array of content items containing image data, titles, and optional descriptions for grid display.
 * @param props.id     Unique identifier for the image grid block used for block intro functionality and component identification.
 * @param props.intro  Module intro configuration object containing introductory content and settings for the block.
 * @param props.testId Unique identifier for testing purposes to enable reliable component identification during testing.
 * @returns The CardGridBlock component JSX structure with intro section and responsive card grid layout.
 *
 * @example
 * ```tsx
 * <CardGridBlock
 *     cards={[
 *         {
 *             id: '1',
 *             image: { alt: 'Sample image', url: '/image.jpg' },
 *             title: 'Card Title',
 *             description: 'Optional description content'
 *         }
 *     ]}
 *     id="card-grid-section"
 *     intro={{ title: 'Grid Section', description: 'Introduction text' }}
 *     testId="CardGridBlock"
 * />
 * ```
 */
const CardGridBlock = observer(({
    cards,
    id,
    intro,
    testId = 'CardGridBlock'
}: ComponentProps) => (
    <Container>
        <BlockIntro blockId={id} intro={intro} testId={`${testId}-Intro`} />
        <ViewportTrigger baseComponent={CardGrid}>
            {cards.map(card => (
                <Card key={card.id ?? ''} bgImage={card.hasImageBackground ? card.image : undefined} span={6}>
                    {!card.hasImageBackground && (
                        <ImageWrapper>
                            <PayloadImage
                                expectedSizes={{lg: 57, md: 57, sm: 57, xl: 57, xs: 57, xxl: 57}}
                                fetchpriority="low"
                                image={card.image}
                                isLazy
                            />
                        </ImageWrapper>
                    )}
                    {card.hasImageBackground && (
                        <Spacer y={20} />
                    )}
                    <Spacer y={6} />
                    <MotionH4 as="h3" variants={FadeInAnimation}>{card.title}</MotionH4>
                    <Spacer y={2} />
                    <motion.div variants={FadeInAnimation}>
                        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                        {card.description && <PayloadLexical description={card.description} />}
                    </motion.div>
                </Card>
            ))}
        </ViewportTrigger>
    </Container>
));

CardGridBlock.displayName = 'CardGridBlock';

export {CardGridBlock};

const CardGrid = styled.div`
    column-gap: ${spacing(6)};
    display: grid;
    grid-auto-rows: auto auto auto auto auto ${spacing(6)};
    grid-template-columns: repeat(auto-fit, minmax(25rem, 40rem));
    grid-template-rows: auto auto auto auto auto ${spacing(6)};
    margin-inline: auto;
    max-width: 100%;
    width: fit-content;
`;

const ImageWrapper = styled.div`
    align-items: center;
    border-radius: 22px;
    display: flex;
    height: 7.9rem;
    justify-content: center;
    padding: 11px;
    width: 7.9rem;
`;

const MotionH4 = H4.withComponent(motion.create(H4));